/**
 * Today — the home page. One job: answer "what needs me today, and is the
 * machine running?" in five seconds, then route anywhere in one click.
 * Design: docs/plans/2026-08-24-dashboard-home-and-navigation-design.md.
 * The masthead states the computed need in words; the attention ledger
 * ranks it in dollars; the operations spine shows the automated day ticking.
 */
import { Link, Navigate } from '@tanstack/react-router';
import {
  FileText,
  FileWarning,
  PackageCheck,
  PackageOpen,
  RefreshCw,
  Truck,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { AppShell } from '../../components/AppShell';
import { useMe, useModuleEnabled } from '../../lib/auth';
import {
  attentionHeadline,
  buildAttentionItems,
  latestSettlementDisplay,
  unitFlowDisplay,
  useDashboard,
} from '../../lib/dashboard';
import { netUnrecoveredForDisplay } from '../../lib/shipments';
import { useStrandedInventory } from '../../lib/inventory';
import { Badge } from '../../design';
import { StrandedInventoryPanel } from './StrandedInventoryPanel';
import { MarginFlowCard } from './MarginFlowCard';
import { InsightsCard } from './InsightsCard';
import { Skeleton } from '../../components/Skeleton';
import type { ActivityItem, DashboardResponse } from '@sourcito/schemas';

// Tailwind's scanner only picks up literal class names — a template string
// like `rise-${i}` is invisible to it, so the rise-4..6 utilities got purged
// in production. These literals keep them alive.
const RISE_DELAYS = ['rise-1', 'rise-2', 'rise-3', 'rise-4', 'rise-5', 'rise-6'];

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const timeAgo = (iso: string, now: Date): string => {
  const mins = Math.floor((now.getTime() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const timeUntil = (iso: string, now: Date): string => {
  const mins = Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / 60000));
  if (mins < 60) return `in ${mins}m`;
  return `in ${Math.floor(mins / 60)}h ${mins % 60}m`;
};

/** "Inventory value at cost" KPI caption. The SKU counts are only honest
 * once the API has actually walked the SKU set — omit the "across N of M"
 * clause (rather than showing a misleading 0 of 0) when either count is
 * null (deploy skew or the module simply hasn't produced counts). */
export function inventoryCaption(
  valuedSkuCount: number | null,
  totalSkuCount: number | null
): string {
  if (valuedSkuCount == null || totalSkuCount == null) return 'at cost';
  return `at cost · across ${valuedSkuCount} of ${totalSkuCount} SKUs`;
}

const ACTIVITY_ICONS: Record<ActivityItem['kind'], LucideIcon> = {
  receipt: PackageCheck,
  shipment: Truck,
  claim: FileWarning,
  sync: RefreshCw,
};

/** One quiet sentence under the headline: is the machine running? */
function machineLine(data: DashboardResponse, now: Date): string {
  const run = data.sync?.lastRun;
  if (!run) return data.sync ? 'Amazon sync has not run yet.' : '';
  if (run.status === 'error') return 'The Amazon sync hit an error — see below.';
  const ageH = (now.getTime() - new Date(run.startedAt).getTime()) / 3_600_000;
  if (ageH > 3) return 'The Amazon sync looks stale — see below.';
  return `The machine is running — synced ${timeAgo(run.startedAt, now)}.`;
}

export function DashboardPage() {
  const { data: me } = useMe();
  const { data, isLoading, isError } = useDashboard(me?.user.role !== 'warehouse');
  const { data: stranded } = useStrandedInventory(me?.user.role !== 'warehouse');
  const ordersEnabled = useModuleEnabled('orders');
  const now = new Date();
  const unitFlow = data ? unitFlowDisplay(data) : null;
  const latestSettlement = data ? latestSettlementDisplay(data.positions) : null;

  // The dashboard is a tenant-side money surface; warehouse users have
  // their own home.
  if (me?.user.role === 'warehouse') return <Navigate to="/app/receiving" />;

  const items = data ? buildAttentionItems(data, now, { ordersEnabled }) : [];

  return (
    <AppShell>
      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
        {/* Masthead */}
        <header className="rise">
          <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">
            {now.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
            {me?.tenant?.name ? ` · ${me.tenant.name}` : ''}
          </p>
          <h1 className="font-display text-4xl sm:text-[44px] font-medium leading-[1.08] tracking-tight mt-3">
            {isLoading ? ' ' : isError ? 'Today is unavailable.' : attentionHeadline(items.length)}
          </h1>
          <p className="text-sm text-fg-secondary mt-3">
            {isError
              ? 'The dashboard call failed — reload, or check the API.'
              : data
                ? machineLine(data, now)
                : ' '}
          </p>
        </header>

        <div className="mt-10 grid gap-10 md:grid-cols-[1fr,260px]">
          {/* Attention ledger */}
          <section aria-label="Needs you">
            <h2 className="text-xs uppercase tracking-[0.18em] text-fg-tertiary mb-3">Needs you</h2>
            {items.length === 0 && !isLoading && !isError && (
              <div className="rise rise-1 bg-surface border border-border-subtle rounded-xl px-5 py-6">
                <p className="text-sm text-fg-secondary">
                  All clear. Claims are filed, shipments are on pace, and the sync is green.
                </p>
              </div>
            )}
            <ul className="bg-surface border border-border-subtle rounded-xl overflow-hidden divide-y divide-border-subtle">
              {items.map((item, i) => {
                const rowContent = (
                  <>
                    <span
                      aria-hidden="true"
                      className={`text-xs font-medium rounded-full px-2.5 py-0.5 shrink-0 ${
                        item.severity === 'danger'
                          ? 'bg-negative/[0.12] text-negative'
                          : 'bg-warning/[0.14] text-warning'
                      }`}
                    >
                      {item.severity === 'danger' ? 'Urgent' : 'Soon'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-fg-primary truncate">{item.title}</span>
                      <span
                        className={`block text-xs ${
                          item.severity === 'danger' ? 'text-negative' : 'text-fg-tertiary'
                        }`}
                      >
                        {item.detail}
                      </span>
                    </span>
                    {item.amountCents != null && (
                      <span className="font-display text-base shrink-0">
                        {money(item.amountCents)}
                      </span>
                    )}
                  </>
                );
                return (
                  <li key={item.key} className={`rise ${RISE_DELAYS[Math.min(i, 5)]}`}>
                    {item.href == null ? (
                      <div className="flex items-center gap-4 py-3.5 px-1">{rowContent}</div>
                    ) : (
                      <Link
                        to={item.href}
                        className="flex items-center gap-4 py-3.5 px-1 group hover:bg-surface-elev transition-colors duration-fast"
                      >
                        {rowContent}
                        <span
                          aria-hidden="true"
                          className="text-fg-tertiary group-hover:text-fg-secondary group-hover:translate-x-0.5 transition-transform duration-fast"
                        >
                          →
                        </span>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Operations spine */}
          <section aria-label="The machine" className="rise rise-2">
            <h2 className="text-xs uppercase tracking-[0.18em] text-fg-tertiary mb-3">
              The machine
            </h2>
            <ol className="relative border-l border-border-default pl-5 space-y-6">
              {data?.sync?.lastRun && (
                <li className="relative">
                  <span
                    aria-hidden="true"
                    className={`absolute -left-[25px] top-1 h-2 w-2 rounded-full ${
                      data.sync.lastRun.status === 'error' ? 'bg-negative' : 'bg-positive'
                    }`}
                  />
                  <p className="text-sm text-fg-primary">
                    {data.sync.lastRun.status === 'error' ? 'Amazon sync failed' : 'Amazon sync'}
                  </p>
                  <p className="font-mono text-xs text-fg-tertiary tabular-nums mt-0.5">
                    {timeAgo(data.sync.lastRun.startedAt, now)}
                    {data.sync.lastRun.status === 'ok' &&
                      ` · ${data.sync.lastRun.inventoryCount} SKUs`}
                  </p>
                </li>
              )}
              {data?.ingest?.lastPoAt && (
                <li className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute -left-[25px] top-1 h-2 w-2 rounded-full bg-positive"
                  />
                  <p className="text-sm text-fg-primary">
                    {data.ingest.lastPoVendor ?? 'Vendor'} invoice ingested
                  </p>
                  <p className="font-mono text-xs text-fg-tertiary tabular-nums mt-0.5">
                    {timeAgo(data.ingest.lastPoAt, now)}
                  </p>
                </li>
              )}
              {data?.sync && (
                <li className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute -left-[25px] top-1 h-2 w-2 rounded-full bg-accent breathe"
                  />
                  <p className="text-sm text-fg-secondary">Next sync</p>
                  <p className="font-mono text-xs text-fg-tertiary tabular-nums mt-0.5">
                    {timeUntil(data.sync.nextSyncAt, now)}
                  </p>
                </li>
              )}
              {!data?.sync && !data?.ingest?.lastPoAt && !isLoading && (
                <li className="relative">
                  <p className="text-sm text-fg-tertiary">Nothing automated yet.</p>
                </li>
              )}
            </ol>
          </section>
        </div>

        {stranded && <StrandedInventoryPanel data={stranded} />}

        {/* KPI row — inventory value, revenue, purchase spend, recoverable. */}
        <section
          aria-label="Key numbers"
          className="rise rise-3 mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <KpiValueCard
            label="Inventory value at cost"
            valueCents={data?.positions.inventoryValueCents ?? null}
            caption={inventoryCaption(
              data?.positions.inventoryValuedSkuCount ?? null,
              data?.positions.inventoryTotalSkuCount ?? null
            )}
          />
          <KpiValueCard
            label="Revenue this month"
            valueCents={data?.positions.revenueMonthCents ?? null}
            caption="gross product sales"
          />
          <KpiValueCard
            label="Purchase spend this month"
            valueCents={data?.positions.purchaseSpendMonthCents ?? null}
            caption="orders placed this month"
          />
          <div className="bg-surface border border-border-subtle rounded-xl p-5">
            <p className="text-xs text-fg-tertiary">Recoverable in claims</p>
            {data?.positions.recoverableCents != null ? (
              <p className="font-display text-2xl text-fg-primary mt-1.5">
                {money(data.positions.recoverableCents)}
              </p>
            ) : (
              <Skeleton className="h-8 w-28 rounded-md mt-2" />
            )}
          </div>
        </section>

        {/* Chart + advisor row — cash & margin flow, insights. */}
        <section
          aria-label="Cash, margin, and insights"
          className="rise rise-4 mt-4 grid gap-4 lg:grid-cols-[1fr,320px]"
        >
          <MarginFlowCard marginFlow={data?.marginFlow} />
          <InsightsCard insights={data?.insights} />
        </section>

        {/* The business by area — live gauges beside the instruments still to
            come, so the cockpit shows the whole operation, built and unbuilt. */}
        {data && (
          <section
            aria-label="Business areas"
            className="rise rise-5 mt-4 grid gap-4 sm:grid-cols-2"
          >
            <AreaPanel title="Procurement" hue="procurement">
              {data.positions.openPoCents != null && (
                <Position
                  value={money(data.positions.openPoCents)}
                  label={`open across ${data.positions.openPoCount} PO${data.positions.openPoCount === 1 ? '' : 's'}`}
                  href="/app/orders"
                />
              )}
              <ComingRow
                title="Discovery pulse"
                description="New qualified brands surfaced this week."
                phase="Soon"
              />
              <ComingRow
                title="KeHE catalog match"
                description="Discovered brands you can order today through your KeHE account."
                phase="Soon"
              />
            </AreaPanel>
            <AreaPanel title="Ops" hue="ops">
              {data.positions.fulfillableUnits != null && (
                <Position
                  value={data.positions.fulfillableUnits.toLocaleString('en-US')}
                  label="units at FBA"
                  href="/app/amazon"
                />
              )}
              {data.positions.openShipmentCount != null && (
                <Position
                  value={String(data.positions.openShipmentCount)}
                  label={`open shipment${data.positions.openShipmentCount === 1 ? '' : 's'}`}
                  href="/app/shipments"
                />
              )}
              {data.positions.fulfillableUnits == null &&
                data.positions.openShipmentCount == null && (
                  <ComingRow
                    title="Amazon sync & shipments"
                    description="Hourly inventory, transit, and check-ins once the Amazon and Shipments modules are on."
                    phase="Module"
                  />
                )}
            </AreaPanel>
            <AreaPanel title="Warehouse" hue="warehouse">
              <ComingRow
                title="Receiving activity"
                description="Receipts, shorts, and damage recorded this week."
                phase="Soon"
              />
            </AreaPanel>
            <AreaPanel title="Finance" hue="finance">
              {data.positions.recoverableCents != null && (
                <Position
                  value={money(data.positions.recoverableCents)}
                  label="recoverable in claims"
                  href={ordersEnabled ? '/app/orders' : null}
                />
              )}
              {data.positions.recoveredCents != null && data.positions.recoveredCents > 0 && (
                <Position
                  value={money(data.positions.recoveredCents)}
                  label="recovered to date"
                  href={ordersEnabled ? '/app/orders' : null}
                />
              )}
              {data.positions.netUnrecoveredCents != null && (
                <Position
                  value={money(netUnrecoveredForDisplay(data.positions.netUnrecoveredCents))}
                  label="net unrecovered at FBA"
                  href="/app/shipments"
                />
              )}
              {latestSettlement && (
                <div>
                  <Position
                    value={latestSettlement.value}
                    label={`latest Amazon settlement · ${latestSettlement.date} · ${latestSettlement.status}`}
                    href="/app/finance"
                  />
                  <p className="text-[11px] text-fg-tertiary mt-1">
                    Amazon report header, not a bank-confirmed deposit.
                  </p>
                </div>
              )}
              <ComingRow
                title="Sales"
                description="Daily units and revenue, pulled from your Amazon orders."
                phase="Phase B"
              />
              {unitFlow?.kind === 'value' ? (
                <Position
                  value={money(unitFlow.residualCents)}
                  label="unit flow inventory variance exposure"
                  href="/app/amazon"
                />
              ) : unitFlow ? (
                <ComingRow
                  title={unitFlow.title}
                  description={unitFlow.description}
                  phase={unitFlow.phase}
                />
              ) : null}
              <ComingRow
                title="Returns"
                description="Removals and rejected inbound flowing back into sellable stock."
                phase="Phase C"
              />
            </AreaPanel>
          </section>
        )}

        {/* Activity feed — receipts, shipments, claims, syncs. Bottom of the
            page (Faraz 2026-09-01): it's a log, not an instrument. */}
        <section aria-label="Activity" className="rise rise-6 mt-4">
          <div className="bg-surface border border-border-subtle rounded-xl p-5">
            <h3 className="text-sm font-medium text-fg-primary">Activity</h3>
            <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">
              Receipts, shipments, claims, and syncs as they happen.
            </p>
            <ActivityFeed activity={data?.activity} now={now} />
          </div>
        </section>
      </main>
    </AppShell>
  );
}

/** A live KPI card: money value when the API has a number, muted skeleton
 * block while it's still loading/null — the pattern "Recoverable in claims"
 * established. `caption` is an optional second line under the value (SKU
 * coverage, "gross product sales", etc.); it never renders under the
 * skeleton. */
export function KpiValueCard({
  label,
  valueCents,
  caption,
}: {
  label: string;
  valueCents: number | null;
  caption?: string;
}) {
  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-5">
      <p className="text-xs text-fg-tertiary">{label}</p>
      {valueCents != null ? (
        <>
          <p className="font-display text-2xl text-fg-primary mt-1.5">{money(valueCents)}</p>
          {caption && <p className="text-xs text-fg-tertiary mt-0.5">{caption}</p>}
        </>
      ) : (
        <Skeleton className="h-8 w-28 rounded-md mt-2" />
      )}
    </div>
  );
}

/** The "Activity" card body: receipts/shipments/claims/syncs, newest first,
 * as the API already sorted them. Three states: `activity` undefined means
 * the dashboard call hasn't resolved yet (skeleton rows, matches the rest
 * of the page's loading convention); an empty array is an honest "nothing
 * happened today" rather than hiding the section; a populated array renders
 * one row per item with a kind icon and relative time. */
export function ActivityFeed({
  activity,
  now,
}: {
  activity: ActivityItem[] | undefined;
  now: Date;
}) {
  if (activity === undefined) {
    return (
      <div className="mt-4 space-y-2" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 rounded-lg" />
        ))}
      </div>
    );
  }
  if (activity.length === 0) {
    return <p className="text-sm text-fg-tertiary mt-4">Nothing yet today.</p>;
  }
  return (
    <ul className="mt-4 space-y-2.5">
      {activity.map((item, i) => {
        const Icon = ACTIVITY_ICONS[item.kind];
        return (
          <li key={`${item.kind}:${item.at}:${i}`} className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-fg-tertiary shrink-0" aria-hidden="true" />
            <span className="text-sm text-fg-primary flex-1 truncate">{item.label}</span>
            <span className="font-mono text-xs text-fg-tertiary shrink-0 tabular-nums">
              {timeAgo(item.at, now)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Position({ value, label, href }: { value: string; label: string; href: string | null }) {
  const content = (
    <>
      <p className="font-display text-2xl text-fg-primary group-hover:text-accent-fg transition-colors duration-fast">
        {value}
      </p>
      <p className="text-xs text-fg-tertiary mt-0.5">{label}</p>
    </>
  );
  if (href == null) return <div>{content}</div>;
  return (
    <Link to={href} className="group">
      {content}
    </Link>
  );
}

/** Per-area gradient + glyph for the AreaPanel header icon tile — the
 *  glyphs match each area's sidebar icon so the two surfaces read as one. */
const AREA_TILES = {
  procurement: { gradient: 'linear-gradient(135deg,#E8B04B,#C97F2E)', Icon: FileText },
  ops: { gradient: 'linear-gradient(135deg,#8FA7F5,#5F7BE0)', Icon: Truck },
  warehouse: { gradient: 'linear-gradient(135deg,#57B98A,#2F8A64)', Icon: PackageOpen },
  finance: { gradient: 'linear-gradient(135deg,#E98268,#C95F48)', Icon: Wallet },
} as const;

type AreaHue = keyof typeof AREA_TILES;

function AreaIcon({ hue }: { hue: AreaHue }) {
  const { gradient, Icon } = AREA_TILES[hue];
  return (
    <span
      className="h-7 w-7 rounded-md shrink-0 flex items-center justify-center"
      style={{ background: gradient }}
      aria-hidden="true"
    >
      <Icon className="h-4 w-4 text-white" />
    </span>
  );
}

function AreaPanel({ title, hue, children }: { title: string; hue: AreaHue; children: ReactNode }) {
  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-5">
      <div className="flex items-center gap-2.5">
        <AreaIcon hue={hue} />
        <h3 className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">{title}</h3>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">{children}</div>
    </div>
  );
}

/** An instrument that exists in the business but not yet in the product. */
function ComingRow({
  title,
  description,
  phase,
}: {
  title: string;
  description: string;
  phase: string;
}) {
  return (
    <div className="w-full border-t border-dashed border-border-default pt-3 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-fg-secondary">{title}</p>
        <Badge variant="neutral">{phase}</Badge>
      </div>
      <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">{description}</p>
    </div>
  );
}
